"use client";

import { useEffect } from "react";
import { Case, CaseListData } from "@repo/provider-network";
import { SubscriptionState } from "src/lib/subscription-gate";
import { useVisitList } from "./hooks/use-visit-list";
import { useUnreadMessages } from "./hooks/use-unread-messages";
import type {
  VisitCardBrandConfig,
  VisitHrefResolver,
} from "./visit-list-item";
import VisitListPure from "./visit-list-pure";

interface VisitListProps {
  initialData?: CaseListData;
  hasTerms?: boolean;
  className?: string;
  showEmptyState?: boolean;
  onStartVisit?: () => void;
  onExplorePlans?: () => void;
  hasSubscription?: boolean;
  subscriptionState?: SubscriptionState;
  startingVisit?: boolean;
  /** Brand-specific visit href resolver. Falls back to default (Kinsa). */
  getVisitHref?: VisitHrefResolver;
  /** Brand-specific card config (drives both active and past visit cards). */
  brandConfig?: VisitCardBrandConfig;
}

/**
 * Main visit list component with SWR for data fetching and caching
 * Implements mobile-first resilience with optimistic updates and fallback data
 */
const VisitList: React.FC<VisitListProps> = ({
  initialData,
  hasTerms = true,
  className = "",
  showEmptyState = true,
  onStartVisit,
  onExplorePlans,
  hasSubscription,
  subscriptionState,
  startingVisit,
  getVisitHref,
  brandConfig,
}) => {
  // Use shared SWR hook for visit list data
  const {
    data: visitData,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useVisitList(initialData, hasTerms);

  // Check for unread messages in active visits
  // Pass isValidating to check messages during SWR revalidation
  const { unreadMap } = useUnreadMessages(visitData, isValidating);

  // Handle app re-focus for revalidation
  useEffect(() => {
    const handleFocus = () => {
      if (!isValidating) {
        mutate();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !isValidating) {
        mutate();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [mutate, isValidating]);

  /**
   * Optimistically add a new visit case when questionnaire is completed
   * TODO: Implement this when questionnaire completion flow is ready
   */
  const _addOptimisticVisit = (newVisit: Case) => {
    if (visitData) {
      const optimisticData: CaseListData = {
        current: [newVisit, ...visitData.current],
        past: visitData.past,
      };

      // Optimistically update the cache
      mutate(optimisticData, false);

      // Then revalidate to get the real data
      setTimeout(() => mutate(), 1000);
    }
  };

  // Use the pure component for rendering
  return (
    <VisitListPure
      visits={visitData}
      isLoading={isLoading}
      error={error}
      unreadMap={unreadMap}
      className={className}
      showEmptyState={showEmptyState}
      onStartVisit={onStartVisit}
      onExplorePlans={onExplorePlans}
      hasSubscription={hasSubscription}
      subscriptionState={subscriptionState}
      startingVisit={startingVisit ?? false}
      getVisitHref={getVisitHref}
      brandConfig={brandConfig}
    />
  );
};

export default VisitList;
